import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader, KPICard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, Clock, ShoppingBag, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  PENDING: "#FFEA00",
  APPROVED: "#00E676",
  REJECTED: "#FF3B30",
  FULFILLED: "#7DD3FC",
  CANCELLED: "#A1A1AA",
};

export default function RedemptionsAdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = React.useState("PENDING");

  const { data: items = [] } = useQuery({
    queryKey: ["redemptions-admin", tab],
    queryFn: async () => (await apiClient.get("/redemptions", { params: tab === "ALL" ? {} : { status_filter: tab } })).data,
  });

  const decide = useMutation({
    mutationFn: async ({ id, decision }) => (await apiClient.patch(`/redemptions/${id}/decision`, { decision })).data,
    onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: ["redemptions-admin"] }); toast.success(v.decision === "APPROVED" ? "Approved & REPS debited" : "Rejected"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const fulfill = useMutation({
    mutationFn: async (id) => (await apiClient.patch(`/redemptions/${id}/fulfill`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["redemptions-admin"] }); toast.success("Marked fulfilled"); },
  });

  const pendingCount = tab === "PENDING" ? items.length : null;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="redemptions-admin-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold flex items-center gap-2">
          <ShoppingBag size={12} /> Rewards
        </div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Redemption Queue</h1>
        <div className="text-zinc-500 text-sm mt-1">
          Members & coaches request redemptions · Super Admin approves to debit REPS · Then Admin fulfils.
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="PENDING" data-testid="tab-pending" className="data-[state=active]:bg-volt data-[state=active]:text-black">Pending {pendingCount != null && `(${pendingCount})`}</TabsTrigger>
          <TabsTrigger value="APPROVED" data-testid="tab-approved" className="data-[state=active]:bg-volt data-[state=active]:text-black">Approved</TabsTrigger>
          <TabsTrigger value="FULFILLED" data-testid="tab-fulfilled" className="data-[state=active]:bg-volt data-[state=active]:text-black">Fulfilled</TabsTrigger>
          <TabsTrigger value="REJECTED" data-testid="tab-rejected" className="data-[state=active]:bg-volt data-[state=active]:text-black">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Panel>
            {items.length === 0 ? (
              <div className="text-zinc-600 text-sm py-12 text-center">No items.</div>
            ) : (
              <div className="space-y-2">
                {items.map((r) => (
                  <div key={r.id} data-testid={`red-${r.id}`} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center gap-3">
                    <div className="w-10 h-10 grid place-items-center rounded-sm" style={{ background: `${STATUS_COLOR[r.status]}22`, color: STATUS_COLOR[r.status] }}>
                      {r.status === "PENDING" ? <Clock size={16} /> : r.status === "APPROVED" ? <CheckCircle2 size={16} /> : r.status === "FULFILLED" ? <PackageCheck size={16} /> : <XCircle size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">
                        {r.user_name || "—"}
                        <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 ml-2">{r.user_role}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                        {r.item_name} · <span className="text-volt font-display text-sm">{r.reps_spent} REPS</span> · {new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-sm" style={{ background: `${STATUS_COLOR[r.status]}22`, color: STATUS_COLOR[r.status] }}>
                      {r.status}
                    </span>
                    {r.status === "PENDING" && user?.role === "SUPER_ADMIN" && (
                      <>
                        <Button size="sm" onClick={() => decide.mutate({ id: r.id, decision: "APPROVED" })} data-testid={`approve-${r.id}`}
                          className="bg-volt hover:bg-volt-hover text-black font-bold uppercase">
                          <CheckCircle2 size={12} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: r.id, decision: "REJECTED" })} data-testid={`reject-${r.id}`}
                          className="bg-zinc-900 border-zinc-800 text-red-400 hover:border-red-500">
                          <XCircle size={12} className="mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {r.status === "APPROVED" && (
                      <Button size="sm" onClick={() => fulfill.mutate(r.id)} data-testid={`fulfill-${r.id}`}
                        className="bg-volt hover:bg-volt-hover text-black font-bold uppercase">
                        <PackageCheck size={12} className="mr-1" /> Mark Fulfilled
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
